import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Container, Box, TextField, Button, Typography, Card, CircularProgress, Link, Tabs, Tab, useTheme, useMediaQuery } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { loginPartner, clearError, sendOTP, verifyLoginOTP } from '../../redux/partnerSlice';
import { RootState, AppDispatch } from '../../redux/store';
import { PhoneInput } from '../../components/PhoneInput';
import RateLimiter from '../../core/auth/RateLimiter';
import AuditLogger from '../../core/auth/AuditLogger';
import ErrorAlert from '../../components/Auth/ErrorAlert';
import PublicHeader from '../../components/Layout/PublicHeader';

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isLoading, error } = useSelector((state: RootState) => state.partner);
  
  const [loginMethod, setLoginMethod] = useState<'mobile' | 'email'>('email');
  const [mobileData, setMobileData] = useState({ phone: '', otp: '' });
  const [emailData, setEmailData] = useState({ email: '', password: '' });
  const [otpSent, setOtpSent] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [rateLimitError, setRateLimitError] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: 'mobile' | 'email') => {
    setLoginMethod(newValue);
    dispatch(clearError());
    setOtpSent(false);
    setLocalError(null);
    setRateLimitError(null);
  };

  const handleSendOTP = async () => {
    if (!mobileData.phone || mobileData.phone.length !== 10) {
      setLocalError('Please enter a valid 10-digit phone number');
      return;
    }

    // Check rate limiting
    if (!RateLimiter.isAllowed('otp-send')) {
      const errorMsg = RateLimiter.getErrorMessage('otp-send');
      setRateLimitError(errorMsg);
      return;
    }

    setOtpLoading(true);
    setLocalError(null);
    setRateLimitError(null);

    try {
      const result = await dispatch(sendOTP(mobileData.phone));
      if (sendOTP.fulfilled.match(result)) {
        setOtpSent(true);
        await AuditLogger.logOTPSend(mobileData.phone, true);
      } else {
        const errorMsg = result.payload as string;
        setLocalError(errorMsg || 'Failed to send OTP');
        await AuditLogger.logOTPSend(mobileData.phone, false);
      }
    } catch (error: any) {
      setLocalError(error.message || 'Failed to send OTP. Please try again.');
      await AuditLogger.logOTPSend(mobileData.phone, false);
    } finally {
      setOtpLoading(false);
    }
  };

  const handleMobileLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otpSent) {
      await handleSendOTP();
      return;
    }

    if (!mobileData.otp || mobileData.otp.length !== 6) {
      setLocalError('Please enter a valid 6-digit OTP');
      return;
    }

    // Check rate limiting
    if (!RateLimiter.isAllowed('otp-verify')) {
      const errorMsg = RateLimiter.getErrorMessage('otp-verify');
      setRateLimitError(errorMsg);
      return;
    }

    setLocalError(null);
    setRateLimitError(null);

    try {
      const result = await dispatch(verifyLoginOTP({ phone: mobileData.phone, otp: mobileData.otp }));
      if (verifyLoginOTP.fulfilled.match(result)) {
        await AuditLogger.logOTPVerify(mobileData.phone, true);
        navigate('/dashboard');
      } else {
        const errorMsg = result.payload as string;
        setLocalError(errorMsg || 'Invalid OTP');
        await AuditLogger.logOTPVerify(mobileData.phone, false);
      }
    } catch (error: any) {
      setLocalError(error.message || 'Login failed. Please try again.');
      await AuditLogger.logOTPVerify(mobileData.phone, false);
    }
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!emailData.email || !emailData.password) {
      setLocalError('Please enter both email and password');
      return;
    }

    // Check rate limiting
    if (!RateLimiter.isAllowed('login')) {
      const errorMsg = RateLimiter.getErrorMessage('login');
      setRateLimitError(errorMsg);
      return;
    }

    setLocalError(null);
    setRateLimitError(null);

    try {
      const result = await dispatch(loginPartner(emailData));
      if (loginPartner.fulfilled.match(result)) {
        await AuditLogger.logLogin(emailData.email, true);
        navigate('/dashboard');
      } else {
        const errorMsg = result.payload as string;
        setLocalError(errorMsg || 'Login failed');
        await AuditLogger.logLogin(emailData.email, false, errorMsg);
      }
    } catch (error: any) {
      setLocalError(error.message || 'Login failed. Please try again.');
      await AuditLogger.logLogin(emailData.email, false, error.message);
    }
  };

  React.useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  return (
    <>
      <Helmet>
        <title>Partner Login — HUSLRZ</title>
      </Helmet>
      <PublicHeader activeLink="login" />
      <Container maxWidth="sm" sx={{ px: { xs: 2, sm: 3 } }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: 'calc(100vh - 60px)',
          py: { xs: 3, sm: 0 }
        }}>
        <Card sx={{ 
          p: { xs: 3, sm: 4 }, 
          width: '100%',
          boxShadow: { xs: 2, sm: 3 }
        }}>
          <Typography 
            variant={isMobile ? 'h5' : 'h4'} 
            sx={{ mb: 3, textAlign: 'center', fontWeight: 'bold' }}
          >
            Partner Login
          </Typography>

          <Tabs 
            value={loginMethod} 
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{ 
              mb: 3,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontSize: { xs: '0.9rem', sm: '1rem' },
                fontWeight: 600
              },
              '& .Mui-selected': {
                color: '#14B8A6'
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#14B8A6'
              }
            }}
          >
            <Tab label="Mobile + OTP" value="mobile" />
            <Tab label="Email + Password" value="email" />
          </Tabs>

          {(error || localError || rateLimitError) && (
            <ErrorAlert
              message={error || localError || rateLimitError || ''}
              severity={rateLimitError ? 'warning' : 'error'}
              onClose={() => {
                dispatch(clearError());
                setLocalError(null);
                setRateLimitError(null);
              }}
            />
          )}

          {loginMethod === 'mobile' ? (
            <form onSubmit={handleMobileLogin}>
              <PhoneInput
                value={mobileData.phone}
                onChange={(value: string) => setMobileData({ ...mobileData, phone: value })}
                error={mobileData.phone.length > 0 && mobileData.phone.length !== 10}
                helperText={mobileData.phone.length > 0 && mobileData.phone.length !== 10 ? 'Phone must be 10 digits' : ''}
                disabled={otpSent}
              />

              {otpSent && (
                <TextField
                  fullWidth
                  label="Enter OTP"
                  value={mobileData.otp}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setMobileData({ ...mobileData, otp: value });
                  }}
                  margin="normal"
                  required
                  inputProps={{ maxLength: 6 }}
                  helperText="Check backend console for OTP (development mode)"
                />
              )}

              <Button
                fullWidth
                variant="contained"
                sx={{ 
                  mt: 3, 
                  height: '48px',
                  fontSize: { xs: '1rem', sm: '1rem' },
                  fontWeight: 600,
                  backgroundColor: '#14B8A6',
                  '&:hover': {
                    backgroundColor: '#0F9688'
                  }
                }}
                type="submit"
                disabled={otpLoading || isLoading || (otpSent && mobileData.otp.length !== 6)}
              >
                {otpLoading || isLoading ? (
                  <CircularProgress size={24} sx={{ color: 'white' }} />
                ) : otpSent ? (
                  'Verify & Login'
                ) : (
                  'Send OTP'
                )}
              </Button>

              {otpSent && (
                <Button
                  fullWidth
                  variant="text"
                  sx={{ 
                    mt: 2,
                    color: '#14B8A6',
                    textTransform: 'none'
                  }}
                  onClick={() => {
                    setOtpSent(false);
                    setMobileData({ ...mobileData, otp: '' });
                  }}
                >
                  Change Phone Number
                </Button>
              )}
            </form>
          ) : (
            <form onSubmit={handleEmailLogin}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={emailData.email}
                onChange={(e) => setEmailData({ ...emailData, email: e.target.value })}
                margin="normal"
                required
              />

              <TextField
                fullWidth
                label="Password"
                name="password"
                type="password"
                value={emailData.password}
                onChange={(e) => setEmailData({ ...emailData, password: e.target.value })}
                margin="normal"
                required
              />

              <Button
                fullWidth
                variant="contained"
                sx={{ 
                  mt: 3, 
                  height: '48px',
                  fontSize: { xs: '1rem', sm: '1rem' },
                  fontWeight: 600,
                  backgroundColor: '#14B8A6',
                  '&:hover': {
                    backgroundColor: '#0F9688'
                  }
                }}
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Login'}
              </Button>
            </form>
          )}

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ fontSize: { xs: '0.85rem', sm: '0.875rem' } }}>
              Don't have an account?{' '}
              <Link
                onClick={() => navigate('/signup')}
                sx={{ 
                  cursor: 'pointer', 
                  color: '#14B8A6', 
                  fontWeight: 'bold',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                Sign up here
              </Link>
            </Typography>
          </Box>
        </Card>
      </Box>
    </Container>
    </>
  );
};

export default LoginPage;
